import React, {useEffect, useMemo, useRef, useState} from 'react';
import {DropdownMultiSelect, MultiFilterButton, SimpleDropdown} from '../../../common';
import {BusinessContactFilterValue, BusinessContactStatusMapWithDefault, EMPTY_FILTER} from './domain/filter';
import {BusinessFunctionOption} from './useBusinessFunctionOptions';

type Props = {
    /** The applied filter. The funnel edits a copy and hands it back only on Apply. */
    initFilterValue: BusinessContactFilterValue;
    options: BusinessFunctionOption[];
    onApply: (filter: BusinessContactFilterValue) => void;
};

/**
 * The funnel: Search, Business Function and Status. Contact Management is the one grid whose search
 * sits inside the funnel rather than beside it, by the PO's call on BHPNPP-3357.
 */
export const BusinessContactFilter = ({initFilterValue, options, onApply}: Props) => {
    const [filterValue, setFilterValue] = useState({...initFilterValue});
    const searchRef = useRef<HTMLInputElement>(null);
    // A chip removed outside the funnel changes the applied filter; the funnel follows it.
    useEffect(() => {
        setFilterValue(initFilterValue);
    }, [initFilterValue]);

    // DropdownMultiSelect clears the boxes a user has ticked whenever its options prop changes
    // identity, so a list rebuilt on every render would throw away picks not yet applied, for example
    // when the options query lands while the funnel is open. Both lists key on what the options say.
    const optionsKey = options.map((option) => `${option.value}|${option.label}`).join(';');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const optionValues = useMemo(() => options.map((option) => option.value), [optionsKey]);
    const labelByValue = useMemo(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        () => new Map(options.map((option) => [option.value, option.label])), [optionsKey]);

    const onFilterUpdate = (field: keyof BusinessContactFilterValue, value: string) =>
        setFilterValue((current) => ({...current, [field]: value}));

    return (
        <MultiFilterButton<BusinessContactFilterValue>
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            emptyFilterValue={EMPTY_FILTER}
            dispatchSetFilterValue={onApply}
            onOpen={() => searchRef.current?.focus()}
            filterOptions={[
                {
                    label: 'Search',
                    subLabel: 'Name, Title, Email or Phone',
                    // Enter here submits the funnel's form, so it applies like the Apply button.
                    component: <input ref={searchRef}
                                      type='text'
                                      aria-label='Search Name, Title, Email or Phone'
                                      autoComplete='off'
                                      className={'block w-full bg-white border border-gray-400 hover:border-atlantis-100 ' +
                                          'px-4 py-2 rounded shadow leading-tight text-sm focus:outline-none focus:shadow-outline'}
                                      value={filterValue.search}
                                      onChange={(e) => onFilterUpdate('search', e.target.value)}/>,
                },
                {
                    label: 'Business Function',
                    component: <DropdownMultiSelect field='businessFunction'
                                                    options={optionValues}
                                                    labelMapperFunc={(value) => labelByValue.get(value) ?? value}
                                                    full
                                                    value={filterValue.businessFunction}
                                                    setValue={(value) => onFilterUpdate('businessFunction', value)}/>,
                },
                {
                    label: 'Status',
                    component: <SimpleDropdown options={BusinessContactStatusMapWithDefault}
                                               selected={filterValue.status}
                                               minWidth={'7rem'}
                                               textColor={'gray-600'}
                                               setSelected={(value) => onFilterUpdate('status', value)}/>,
                },
            ]}
        />
    );
};
